import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Navigate } from "react-router-dom";

import { Input, Button, notification, Spin, Tooltip } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, CheckOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";

import { confirmPassword } from '../../../API/password';

import logo from '../assets/logo-login.svg';
import { useAuth } from "../../Context/AuthContext";
import '../Login.css';

const PasswordReset = () => {
  const navigate = useNavigate();
  const { uid, token } = useParams();
  // Permisos
  const { userData } = useAuth();
  const needsPasswordChange =
    userData?.password_status?.force_change === true ||
    userData?.password_status?.is_expired === true;
  const backendUid = userData?.password_status?.uid;
  const backendToken = userData?.password_status?.token;

  // Estados componente
  const [api, contextHolder] = notification.useNotification();
  const location = useLocation();
  const { state } = location;

  // Determinar si viene desde email o desde login
  const isEmailReset = !!(uid && token);
  const isForceChange = !isEmailReset;

  // Estados de contraseñas
  const [password, setPassword] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);

  // Validaciones de contraseña
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  // Mostrar notificación si viene desde login con force_change o is_expired
  useEffect(() => {
    if (state?.showNotification && isForceChange) {
      api.warning({
        message: "Su contraseña debe ser actualizada",
        description: "Por favor ingrese una nueva contraseña para continuar.",
      });
    }
  }, [state, api, isForceChange]);

  const validatePasswordRules = (password) => {
    setPasswordRules({
      length: password.length >= 8 && password.length <= 20,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const RuleCheck = ({ text, checked }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        fontSize: 12,
        fontWeight: 500,
        color: checked ? "#52c41a" : "#8c8c8c",
        marginBottom: "6px",
        transition: "color 0.3s ease",
      }}
    >
      {checked ? (
        <CheckOutlined style={{ marginRight: "8px", color: "#52c41a" }} />
      ) : (
        <CloseOutlined style={{ marginRight: "8px", color: "#ff4d4f" }} />
      )}
      {text}
    </div>
  );

  const allRulesValid = Object.values(passwordRules).every(Boolean);

  // Contenido del tooltip de reglas
  const passwordRulesContent = (
    <div style={{ padding: "4px 0" }}>
      <div style={{ marginBottom: "8px", fontWeight: 600, fontSize: 13 }}>
        La contraseña debe tener:
      </div>
      <RuleCheck text="Entre 8 y 20 caracteres" checked={passwordRules.length} />
      <RuleCheck text="Al menos una letra mayúscula" checked={passwordRules.uppercase} />
      <RuleCheck text="Al menos una letra minúscula" checked={passwordRules.lowercase} />
      <RuleCheck text="Al menos un número" checked={passwordRules.number} />
      <RuleCheck text="Al menos un caracter especial" checked={passwordRules.special} />
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPasswordValue) {
      api.error({ message: "Por favor complete todos los campos." });
      return;
    }

    if (!allRulesValid) {
      api.error({ message: "La contraseña no cumple con los requisitos de seguridad." });
      return;
    }

    if (password !== confirmPasswordValue) {
      api.error({ message: "Las contraseñas no coinciden." });
      return;
    }

    setLoading(true);

    // CASO 1: Reset desde email (con uid y token en URL)
    if (isEmailReset) {
      const { response, error } = await confirmPassword({ 
        uid, 
        token, 
        new_password: password, 
        confirm_password: confirmPasswordValue 
      });

      if (response) {
        api.success({ message: "Contraseña restablecida correctamente." });
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        api.error({ 
          message: "Error al restablecer la contraseña.",
          description: error?.error || "Intente nuevamente.",
          key: 'password-error'
        });
      }
    } 
    // CASO 2: Reset forzado desde login (force_change/is_expired)
    else {
      //const tokenFromStorage = localStorage.getItem("access_token");
      //const mail = JSON.parse(localStorage.getItem("userData"))?.email;

      if (!backendToken) {
        api.error({ message: "Token no encontrado. Inicie sesión nuevamente." });
        setLoading(false);
        return;
      }

      const { response, error } = await confirmPassword({
        uid: backendUid, 
        token: backendToken, 
        new_password: password,
        confirm_password: confirmPasswordValue, 
      });

      if (response) {
        api.success({ message: "Contraseña restablecida correctamente." });
        setTimeout(() => {
          navigate("/home");
        }, 1500);
      } else {
        api.error({ 
          message: "Error al restablecer la contraseña.",
          description: error?.error || "Intente nuevamente.",
          key: 'password-error'
        });
      }
    }

    setLoading(false);
  };

  // Contenido del formulario
  const formContent = (
    <>
      {contextHolder}
      <div className="login-page">
        <img src={logo} alt="Logo de la aplicación" className="app-logo" />

        <form onSubmit={handleSubmit} className="login-form">
          <h2 className="recovery-title">Restablecer Contraseña</h2>
          <p className="recovery-text">
            {isEmailReset 
              ? "Ingrese su nueva contraseña" 
              : state?.email && `Restableciendo contraseña para: ${state.email}`
            }
          </p>

          {/* Input de Nueva Contraseña con ícono flotante */}
          <div style={{ position: "relative" }}>
            <Input.Password
              placeholder="Nueva Contraseña"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                validatePasswordRules(e.target.value);
              }}
              onFocus={() => setShowPasswordRules(true)}
              onBlur={() => setTimeout(() => setShowPasswordRules(false), 200)}
              size="large"
              className="input-box"
              iconRender={(visible) =>
                visible ? <EyeTwoTone twoToneColor="#91D500" /> : <EyeInvisibleOutlined style={{ color: "#93d534" }} />
              }
            />
            <Tooltip
              title={passwordRulesContent}
              open={showPasswordRules || undefined}
              color="#fff"
              overlayStyle={{ maxWidth: '320px' }}
              overlayInnerStyle={{ 
                color: '#262626',
                boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
              }}
              placement="topRight"
            >
              <InfoCircleOutlined 
                style={{ 
                  position: 'absolute',
                  top: '-24px',
                  right: '0px',
                  color: password ? (allRulesValid ? '#52c41a' : '#faad14') : '#bfbfbf',
                  fontSize: '18px',
                  cursor: 'pointer',
                  transition: 'color 0.3s ease',
                  zIndex: 10
                }}
                onClick={() => setShowPasswordRules(!showPasswordRules)}
                onMouseEnter={() => setShowPasswordRules(true)}
                onMouseLeave={() => setShowPasswordRules(false)}
              />
            </Tooltip>
          </div>

          <Input.Password
            placeholder="Confirmar Contraseña"
            value={confirmPasswordValue}
            onChange={(e) => setConfirmPasswordValue(e.target.value)}
            size="large"
            className="input-box"
            iconRender={(visible) =>
              visible ? <EyeTwoTone twoToneColor="#91D500" /> : <EyeInvisibleOutlined style={{ color: "#93d534" }} />
            }
          />

          <Button
            htmlType="submit"
            className="login-btn"
            size="large"
            block
            disabled={loading || !allRulesValid || !password || !confirmPasswordValue || password !== confirmPasswordValue}
          >
            {loading ? <Spin className="custom-login-spin" size="small" /> : "Guardar Contraseña"}
          </Button>
        </form>
      </div>
    </>
  );

  // Si viene desde EMAIL (con uid/token), renderizar directo
  if (isEmailReset) {
    return formContent;
  }

  // Si viene desde LOGIN pero NO necesita cambiar contraseña, redirigir a home
  if (isForceChange && !needsPasswordChange) {
    return <Navigate to="/home" replace />;
  }

  // Si llegó aquí, el usuario necesita cambiar la contraseña obligatoriamente
  return formContent;
};

export default PasswordReset;